import { Link } from "wouter";
import {
  Award,
  BookOpen,
  Code,
  FileText,
  Github,
  Globe,
  HelpCircle,
  Linkedin,
  Mail,
  MapPin,
  Rocket,
  Shield,
  Trophy,
  Twitter,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { BrandLogo } from "@/components/brand-logo";
import { programmaticSeoPages } from "@shared/seo-content";

export default function Footer() {
  const currentYear = new Date().getFullYear();

  const platformLinks = [
    { href: "/courses", label: "Courses", icon: BookOpen },
    { href: "/starter-kits", label: "Starter Kits", icon: Rocket },
    { href: "/playground", label: "Practice", icon: Code },
    { href: "/career", label: "Career Hub", icon: Users },
    { href: "/certifications", label: "Certifications", icon: Trophy },
    { href: "/roadmap", label: "Roadmap", icon: MapPin },
  ];

  const resourceLinks = [
    { href: "/documentation", label: "Documentation", icon: FileText },
    { href: "/community", label: "Community", icon: Users },
    { href: "/blog", label: "Blog & Tutorials", icon: BookOpen },
    { href: "/research", label: "Research Papers", icon: FileText },
    { href: "/help", label: "Help Center", icon: HelpCircle },
  ];

  const trackLinks = [
    { href: "/track/1", label: "AI Foundations & Discovery" },
    { href: "/track/2", label: "Programming & AI Development" },
    { href: "/track/3", label: "Machine Learning Mastery" },
    { href: "/track/4", label: "Deep Learning & Neural Networks" },
    { href: "/track/9", label: "Python: 100-Day Journey" },
  ];

  const guideLinks = programmaticSeoPages.slice(0, 5).map((page) => ({
    href: page.path,
    label: page.h1,
  }));

  const companyLinks = [
    { href: "/contact", label: "Contact" },
    { href: "/privacy", label: "Privacy" },
    { href: "/terms", label: "Terms" },
    { href: "/accessibility", label: "Accessibility" },
  ];

  return (
    <footer className="border-t bg-slate-50/80 dark:bg-slate-950">
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <Link href="/" className="mb-4 inline-flex rounded-md">
              <BrandLogo markClassName="h-11 w-11" />
            </Link>

            <p className="max-w-sm text-sm leading-6 text-muted-foreground">
              Practical AI education for builders, professionals, and teams.
              Learn with structured courses, real projects, certifications, and
              career-ready practice.
            </p>

            <div className="mt-6 space-y-3 text-sm">
              <div className="flex items-center gap-2">
                <Award className="h-4 w-4 shrink-0 text-blue-600" />
                <span>Professional certificates</span>
              </div>
              <div className="flex items-center gap-2">
                <Globe className="h-4 w-4 shrink-0 text-emerald-600" />
                <span>75+ AI courses in active tracks</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4 shrink-0 text-purple-600" />
                <span>Secure account protection</span>
              </div>
            </div>
          </div>

          <div>
            <h3 className="mb-4 font-semibold">Platform</h3>
            <ul className="space-y-2">
              {platformLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
                  >
                    <link.icon className="h-3.5 w-3.5 shrink-0" />
                    <span>{link.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="mb-4 font-semibold">Resources</h3>
            <ul className="space-y-2">
              {resourceLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
                  >
                    <link.icon className="h-3.5 w-3.5 shrink-0" />
                    <span>{link.label}</span>
                  </Link>
                </li>
              ))}
            </ul>

            <div className="mt-6 space-y-2 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 shrink-0" />
                <span className="break-all">support@aiacademypro.online</span>
              </div>
              <div className="flex items-center gap-2">
                <Globe className="h-4 w-4 shrink-0" />
                <span>aiacademypro.online</span>
              </div>
            </div>
          </div>

          <div>
            <h3 className="mb-4 font-semibold">Learning Tracks</h3>
            <ul className="space-y-2">
              {trackLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>

            <Link href="/payment-plans" className="mt-4 inline-block">
              <Button size="sm">View All Tracks</Button>
            </Link>

            <div className="mt-6">
              <h3 className="mb-4 font-semibold">AI Learning Guides</h3>
              <ul className="space-y-2">
                {guideLinks.map((link) => (
                  <li key={link.href}>
                    <Link
                      href={link.href}
                      className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <Separator className="my-8" />

        <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              © {currentYear} AI Academy Pro. All rights reserved.
            </p>
            <div className="flex flex-wrap gap-x-4 gap-y-2">
              {companyLinks.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="text-xs text-muted-foreground transition-colors hover:text-foreground"
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <a
              href="https://twitter.com/aiacademypro"
              className="rounded-md p-2 text-muted-foreground transition-colors hover:bg-background hover:text-foreground"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="AI Academy Pro on Twitter"
            >
              <Twitter className="h-4 w-4" />
            </a>
            <a
              href="https://github.com/aiacademypro"
              className="rounded-md p-2 text-muted-foreground transition-colors hover:bg-background hover:text-foreground"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="AI Academy Pro on GitHub"
            >
              <Github className="h-4 w-4" />
            </a>
            <a
              href="https://linkedin.com/company/aiacademypro"
              className="rounded-md p-2 text-muted-foreground transition-colors hover:bg-background hover:text-foreground"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="AI Academy Pro on LinkedIn"
            >
              <Linkedin className="h-4 w-4" />
            </a>
          </div>
        </div>

        <div className="mt-6 border-t pt-6">
          <p className="mx-auto max-w-4xl text-center text-xs leading-5 text-muted-foreground">
            AI Academy Pro provides structured artificial intelligence education
            for learners from beginners to enterprise leaders, with practical
            projects, professional certificates, and hands-on platform tools.
          </p>
          <p className="mt-3 text-center text-xs text-muted-foreground">
            Built by{" "}
            <a
              href="https://nimbusweave.com"
              className="font-medium text-foreground underline-offset-4 transition-colors hover:text-blue-700 hover:underline dark:hover:text-cyan-300"
              target="_blank"
              rel="noopener noreferrer"
            >
              NimbusWeave
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}
